import { debounce, uniqueId } from "lodash"
import * as React from "react"
import { Control, Emitter } from "rete"
import { EventsTypes } from "rete/types/events"

interface BooleanFieldProps {
  getValue: () => boolean
  setValue: (value: boolean) => void
  label: string
}

const BooleanField = ({ getValue, setValue, label }: BooleanFieldProps) => {
  const [, forceUpdate] = React.useReducer(x => x + 1, 0)
  // Label-only ids collide when multiple nodes share the same control label
  // (e.g. two "And equal to" checkboxes). useId is React 18+; keep a stable
  // unique id per mount for React 17.
  const inputId = React.useRef(uniqueId("boolean-control-")).current
  const checked = getValue()

  return (
    <label
      className={`node-boolean-switch${checked ? " is-on" : ""}`}
      htmlFor={inputId}
      onPointerDown={e => e.stopPropagation()}
      onDoubleClick={e => e.stopPropagation()}
    >
      <input
        type="checkbox"
        className="node-boolean-switch__input"
        id={inputId}
        checked={checked}
        onChange={e => {
          setValue(e.target.checked)
          forceUpdate()
        }}
      />
      <span className="node-boolean-switch__track" aria-hidden>
        <span className="node-boolean-switch__thumb" />
      </span>
      <span className="node-boolean-switch__label">{label}</span>
    </label>
  )
}

export class BooleanControl extends Control {
  props: BooleanFieldProps
  component: (props: BooleanFieldProps) => JSX.Element
  type: string

  constructor(emitter: Emitter<EventsTypes> | null, key: string, label: string) {
    super(key)
    this.type = "BooleanControl"

    // "process" persists the model via writeModel; full engine run is not tied to this event.
    const notify = debounce(() => emitter?.trigger("process"), 300)
    this.props = {
      getValue: () => Boolean(this.getData(key)),
      setValue: value => {
        this.putData(key, value)
        notify()
      },
      label,
    }
    this.component = BooleanField
  }
}
